import * as Sentry from '@sentry/angular';

/**
 * One backend failure used to reach Sentry twice: the HTTP interceptor captured the raw
 * message of every 500 body, and the caller then handed the same text to
 * AppService.showError, which throws it wrapped in the localized string shown to the
 * user. The two events carried different messages, so they landed in two groups, and
 * neither grouped by the actual defect - Sentry fingerprints the interceptor's events by
 * its own stack frame, which dumped every distinct backend defect into one bucket
 * (issue #320, group 5321: timeouts, deadlocks, FK violations and null references
 * together). Volatile ids inside the text ("Process ID 153") split the rest.
 *
 * So the raw backend text travels separately from the user-facing string: this module
 * turns it into a stable grouping key, and the interceptor stays quiet when showError
 * already owns the message - showError's event is the richer one, it carries the fault
 * number the user can read off the screen.
 */

/** Delay before the interceptor reports, i.e. how long showError has to claim a message. */
const CLAIM_WINDOW_MS = 1500;

const VOLATILE: [RegExp, string][] = [
    // ahead of the digit runs, which would take a GUID's leading group and leave the rest varying
    [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<guid>'],
    [/Process ID \d+/gi, 'Process ID <n>'],
    [/\d{4,}/g, '<n>'],
];

interface IPendingReport {
    message: string;
    key: string;
    at: number;
    claimed: boolean;
}

let pending: IPendingReport[] = [];

export interface IBackendError extends Error {
    backendErrorKey?: string;
}

function asText(message: unknown): string {
    return typeof message === 'string' ? message : String(message || '');
}

/** Grouping key for a backend message: the text without the parts that vary per occurrence. */
export function backendErrorKey(message: unknown): string {
    return VOLATILE.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), asText(message))
        // the key also rides along as a Sentry tag, which has no room for newlines
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 200);
}

/**
 * Takes the message over from the interceptor, and answers with the key to group by.
 * Callers wrap the backend text in their own context ("code: 500, error: <text>") before
 * handing it to showError, so a pending report counts as the same failure when its
 * message is contained in this one - that is what keeps both events, and both group keys,
 * pointing at the backend defect rather than at whoever relayed it.
 */
export function claimBackendError(message: unknown): string {
    const text = asText(message);
    const hit = pending.filter(report => !report.claimed && report.message && text.indexOf(report.message) !== -1)[0];
    if (hit) {
        hit.claimed = true;
        return hit.key;
    }
    return backendErrorKey(text);
}

/** Reports a backend message unless a caller claims it first. */
export function reportBackendError(message: string, extras: Record<string, string>): void {
    const report: IPendingReport = { message, key: backendErrorKey(message), at: Date.now(), claimed: false };
    pending = pending.filter(old => report.at - old.at <= CLAIM_WINDOW_MS * 2);
    pending.push(report);
    setTimeout(() => {
        if (report.claimed) {
            return;
        }
        const error: IBackendError = new Error(message);
        error.backendErrorKey = report.key;
        Sentry.withScope(scope => {
            scope.setExtra('errorExtraData', error);
            Object.keys(extras).forEach(name => scope.setExtra(name, extras[name]));
            Sentry.captureException(error);
        });
    }, CLAIM_WINDOW_MS);
}
